import { ChartDataPoint, ChartSeries } from 'components/Charts/types';
import {
  formatCurrency,
  getPeriodStartDate,
  getQuarterEnd,
  Period,
  PERIODS,
  PeriodValue,
  round2,
  toNum,
} from '../MutualFundMarksCard/MutualFundMarksCard.utils';

export { formatCurrency, getPeriodStartDate, Period, PERIODS, toNum };
export type { PeriodValue };

export interface ZanbatoReportedMarkItem {
    report_period: string | null;
    reported_marks_max: string | null;
    reported_marks_min: string | null;
    reported_marks_median: string | null;
    reported_marks_dollar_weighted_average: string | null;
    reported_marks_simple_average: string | null;
}

export interface ZanbatoReportedMarksResponse {
    zanbato_id: string | null;
    marks: ZanbatoReportedMarkItem[];
}

export interface ZanbatoMarksValuationItem {
    name: string | null;
    date: string | null;
    security: string | null;
    total_value: string | null;
    price_per_share: string | null;
}

export interface ZanbatoMarksValuationResponse {
    zanbato_id: string | null;
    valuations: ZanbatoMarksValuationItem[];
}

interface SeriesConfig {
    id: string;
    label: string;
    color: string;
    field: keyof Omit<ZanbatoReportedMarkItem, 'report_period'>;
}

export const SERIES_CONFIGS: SeriesConfig[] = [
  {id: 'weighted_avg', label: 'Weighted Avg', color: '#7b1fa2', field: 'reported_marks_dollar_weighted_average'},
  {id: 'median', label: 'Median', color: '#1976d2', field: 'reported_marks_median'},
  {id: 'simple_avg', label: 'Simple Avg', color: '#0097a7', field: 'reported_marks_simple_average'},
  {id: 'max', label: 'Max', color: '#2e7d32', field: 'reported_marks_max'},
  {id: 'min', label: 'Min', color: '#d32f2f', field: 'reported_marks_min'},
];

export const DEFAULT_ENABLED_IDS = new Set(['weighted_avg']);

const GRAY_AREA = {areaStyle: {color: 'rgba(0, 0, 0, 0.06)'}, connectNulls: true};
const CONNECT_NULLS = {connectNulls: true};

export interface ValuationChartSeriesResult {
    series: ChartSeries[];
    singleFund: boolean;
}

/**
 * Builds chart series from valuation items, mirroring the Edgar fund-marks pattern:
 * - Single fund in input → one line at raw dates with a gray area fill.
 * - Multiple funds → Weighted Avg / Max / Min lines aggregated to quarter-end dates,
 *   weighted by total_value.
 */
export const buildValuationChartSeries = (valuations: ZanbatoMarksValuationItem[]): ValuationChartSeriesResult => {
  const valid = valuations.filter(v => v.date && toNum(v.price_per_share) !== null);
  const uniqueNames = [...new Set(valid.map(v => v.name ?? 'Unknown'))];

  if (uniqueNames.length === 0) {
    return {series: [], singleFund: false};
  }

  if (uniqueNames.length === 1) {
    const byDate = new Map<string, { sumValue: number; sumWeight: number }>();
    valid.forEach(v => {
      const pps = toNum(v.price_per_share)!;
      const weight = Math.max(toNum(v.total_value) ?? 1, 1);
      const prev = byDate.get(v.date!) ?? {sumValue: 0, sumWeight: 0};
      byDate.set(v.date!, {sumValue: prev.sumValue + pps * weight, sumWeight: prev.sumWeight + weight});
    });
    const data: ChartDataPoint[] = [...byDate.keys()].sort().map(date => {
      const {sumValue, sumWeight} = byDate.get(date)!;
      return {x: date, y: round2(sumValue / sumWeight)};
    });
    return {
      singleFund: true,
      series: [{id: 'val_value', label: uniqueNames[0], data, color: '#ed6c02', config: GRAY_AREA}],
    };
  }

  // Multiple funds: aggregate per-fund to quarter-end dates, then compute cross-fund avg/max/min.
  const byFundAndQuarter = new Map<string, Map<string, { sumValue: number; sumWeight: number }>>();
  valid.forEach(v => {
    const pps = toNum(v.price_per_share)!;
    const weight = Math.max(toNum(v.total_value) ?? 1, 1);
    const fund = v.name ?? 'Unknown';
    const quarter = getQuarterEnd(v.date!);
    if (!byFundAndQuarter.has(fund)) {
      byFundAndQuarter.set(fund, new Map());
    }
    const qMap = byFundAndQuarter.get(fund)!;
    const prev = qMap.get(quarter) ?? {sumValue: 0, sumWeight: 0};
    qMap.set(quarter, {sumValue: prev.sumValue + pps * weight, sumWeight: prev.sumWeight + weight});
  });

  const allQuarters = [...new Set([...byFundAndQuarter.values()].flatMap(m => [...m.keys()]))].sort();

  const avgData: ChartDataPoint[] = [];
  const maxData: ChartDataPoint[] = [];
  const minData: ChartDataPoint[] = [];

  allQuarters.forEach(q => {
    const fundEntries = [...byFundAndQuarter.values()]
      .map(qMap => qMap.get(q))
      .filter((e): e is { sumValue: number; sumWeight: number } => e !== undefined);
    if (!fundEntries.length) {
      return;
    }
    const fundPps = fundEntries.map(e => e.sumValue / e.sumWeight);
    const totalWeight = fundEntries.reduce((s, e) => s + e.sumWeight, 0);
    const avg = fundEntries.reduce((s, e) => s + (e.sumValue / e.sumWeight) * e.sumWeight, 0) / totalWeight;
    avgData.push({x: q, y: round2(avg)});
    maxData.push({x: q, y: round2(Math.max(...fundPps))});
    minData.push({x: q, y: round2(Math.min(...fundPps))});
  });

  return {
    singleFund: false,
    series: [
      {id: 'val_avg', label: 'Val. Weighted Avg', data: avgData, color: '#ed6c02', config: GRAY_AREA},
      {id: 'val_max', label: 'Val. Max', data: maxData, color: '#558b2f', config: CONNECT_NULLS},
      {id: 'val_min', label: 'Val. Min', data: minData, color: '#880e4f', config: CONNECT_NULLS},
    ],
  };
};

export const getLastValuationWeightedAvg = (
  valuations: ZanbatoMarksValuationItem[],
  endDate: string | undefined,
): { value: number | null; date: string | null } => {
  const valid = (endDate ? valuations.filter(v => v.date && v.date <= endDate) : valuations)
    .filter(v => v.date && toNum(v.price_per_share) !== null);
  if (!valid.length) {
    return {value: null, date: null};
  }
  const byQuarter = new Map<string, { sumValue: number; sumWeight: number }>();
  valid.forEach(v => {
    const pps = toNum(v.price_per_share)!;
    const weight = Math.max(toNum(v.total_value) ?? 1, 1);
    const q = getQuarterEnd(v.date!);
    const prev = byQuarter.get(q) ?? {sumValue: 0, sumWeight: 0};
    byQuarter.set(q, {sumValue: prev.sumValue + pps * weight, sumWeight: prev.sumWeight + weight});
  });
  const quarters = [...byQuarter.keys()].sort();
  if (!quarters.length) {
    return {value: null, date: null};
  }
  const lastQ = quarters[quarters.length - 1];
  const {sumValue, sumWeight} = byQuarter.get(lastQ)!;
  return {value: round2(sumValue / sumWeight), date: lastQ};
};

const VALUATION_OVERLAY_COLORS = ['#5c6bc0', '#26a69a', '#f57c00', '#8d6e63', '#78909c', '#d81b60'];

export const buildValuationFundOverlaySeries = (
  valuations: ZanbatoMarksValuationItem[],
  funds: string[],
): ChartSeries[] => {
  if (!funds.length) {
    return [];
  }
  return funds.map((fund, i) => {
    const fundVals = valuations.filter(v => v.name === fund && v.date && toNum(v.price_per_share) !== null);
    const byDate = new Map<string, { sumValue: number; sumWeight: number }>();
    fundVals.forEach(v => {
      const pps = toNum(v.price_per_share)!;
      const weight = Math.max(toNum(v.total_value) ?? 1, 1);
      const prev = byDate.get(v.date!) ?? {sumValue: 0, sumWeight: 0};
      byDate.set(v.date!, {sumValue: prev.sumValue + pps * weight, sumWeight: prev.sumWeight + weight});
    });
    const data: ChartDataPoint[] = [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, {sumValue, sumWeight}]) => ({x: date, y: round2(sumValue / sumWeight)}));
    return {
      id: `val_fund_${fund}`,
      label: fund,
      data,
      color: VALUATION_OVERLAY_COLORS[i % VALUATION_OVERLAY_COLORS.length],
      config: {connectNulls: true},
    };
  });
};

export const hasAnyStat = (m: ZanbatoReportedMarkItem): boolean =>
  SERIES_CONFIGS.some(cfg => toNum(m[cfg.field]) !== null);

export const formatPeriodLabel = (period: string): string => {
  const [y, m, d] = period.split('-');
  if (!y || !m) {
    return period;
  }
  return `${m}/${d ?? '01'}/${y}`;
};

export const filterMarksByPeriod = (
  marks: ZanbatoReportedMarkItem[],
  period: PeriodValue,
  endDate: string | undefined,
): ZanbatoReportedMarkItem[] => {
  const dated = endDate
    ? marks.filter(m => m.report_period && m.report_period <= endDate)
    : marks.filter(m => m.report_period);
  if (period === Period.All || !endDate) {
    return dated;
  }
  const startDate = getPeriodStartDate(endDate, period);
  if (!startDate) {
    return dated;
  }
  return dated.filter(m => m.report_period! >= startDate);
};

export const buildChartSeries = (
  marks: ZanbatoReportedMarkItem[],
  enabledIds: Set<string>,
): ChartSeries[] => {
  const sorted = [...marks].sort((a, b) =>
    (a.report_period ?? '').localeCompare(b.report_period ?? ''),
  );
  return SERIES_CONFIGS
    .filter(cfg => enabledIds.has(cfg.id))
    .map(cfg => ({
      id: cfg.id,
      label: cfg.label,
      color: cfg.color,
      config: CONNECT_NULLS,
      data: sorted
        .filter(m => m.report_period && toNum(m[cfg.field]) !== null)
        .map(m => ({x: m.report_period!, y: toNum(m[cfg.field])!})),
    }));
};

const getLatestMarkValue = (
  marks: ZanbatoReportedMarkItem[],
  field: keyof Omit<ZanbatoReportedMarkItem, 'report_period'>,
  endDate: string | undefined,
): { value: number | null; period: string | null } => {
  const dated = endDate
    ? marks.filter(m => m.report_period && m.report_period <= endDate)
    : marks.filter(m => m.report_period);
  const sorted = [...dated].sort((a, b) =>
    (b.report_period ?? '').localeCompare(a.report_period ?? ''),
  );
  const found = sorted.find(m => toNum(m[field]) !== null);
  if (found) {
    return {value: toNum(found[field]), period: found.report_period};
  }
  return {value: null, period: null};
};

export const getLatestWeightedAvg = (
  marks: ZanbatoReportedMarkItem[],
  endDate: string | undefined,
): { value: number | null; period: string | null } =>
  getLatestMarkValue(marks, 'reported_marks_dollar_weighted_average', endDate);

export const computeChange = (
  marks: ZanbatoReportedMarkItem[],
): { abs: number; pct: number } | null => {
  const withValues = marks
    .filter(m => m.report_period && toNum(m.reported_marks_dollar_weighted_average) !== null)
    .sort((a, b) => a.report_period!.localeCompare(b.report_period!));
  if (withValues.length < 2) {
    return null;
  }
  const first = toNum(withValues[0].reported_marks_dollar_weighted_average)!;
  const last = toNum(withValues[withValues.length - 1].reported_marks_dollar_weighted_average)!;
  if (first === 0) {
    return null;
  }
  return {abs: last - first, pct: ((last - first) / first) * 100};
};
